import {
  getQuickListCatalogService,
  processCatalogBatchFileService,
  saveQuickListCatalogService,
} from 'redux/services/quickListCatalogServices'
import { uploadFileService } from 'redux/services/quickListSettingsServices'
import { getListPricesService } from 'redux/services/clientsServices'
import { typePriceLists } from 'Utils/constants'
import {
  CATALOG_BATCH_TEMPLATE_ID,
  buildCatalogImageFormData,
  extractCatalogBatchResult,
  extractUploadedFileUrl,
  filterRegularPriceLists,
  getRegularPriceListCode,
} from 'Views/Settings/QuickList/QuickListCatalog/utils/quickListCatalog.utils'
import {
  BATCH_ENTITY_ID,
  BATCH_ENTITY_TYPE,
  BATCH_TENANT_ID,
} from 'Views/Settings/QuickList/SearchQuickList/constants/quickList.constants'
import {
  buildQuickListBatchUploadFormData,
  extractUploadedBatchFile,
} from 'Views/Settings/QuickList/SearchQuickList/utils/quickList.utils'

const PRICE_LIST_PAGE_SIZE = 999

const getErrorMessage = error => error?.response?.data?.Status?.Message || null

// Devuelve el catálogo de la lista o null si todavía no tiene (incluye un 404). Cualquier otro
// error se propaga: la pantalla no debe asumir que no hay catálogo y arriesgar sobrescribirlo.
export const getQuickListCatalogAction = productCustomListId => async () => {
  try {
    const response = await getQuickListCatalogService(productCustomListId)
    const { data, status } = response
    if (status === 200 && data?.Data?.length > 0) {
      return data.Data[0]
    }
    return null
  } catch (error) {
    if (error?.response?.status === 404) return null
    throw error
  }
}

export const saveQuickListCatalogAction = payload => async () => {
  try {
    const response = await saveQuickListCatalogService(payload)
    const { data, status } = response
    const success = status === 200 && data?.Status?.Code === 200
    return {
      success,
      catalog: success ? data?.Data?.[0] || null : null,
      message: success ? null : data?.Status?.Message || null,
    }
  } catch (error) {
    return { success: false, catalog: null, message: getErrorMessage(error) }
  }
}

// Un error en una consulta deja ese grupo vacío sin impedir la carga de los demás ni de la pantalla.
const fetchPriceLists = async payload => {
  try {
    const response = await getListPricesService({
      PageNumber: 1,
      PageSize: PRICE_LIST_PAGE_SIZE,
      ...payload,
    })
    const { data, status } = response
    return status === 200 && Array.isArray(data?.Data) ? data.Data : []
  } catch (error) {
    return []
  }
}

// Listas seleccionables para el catálogo: base de la compañía, regular web de la compañía,
// tipo catálogo y tipo matriz de descuento.
export const getCatalogPriceListsAction = companyId => async () => {
  const code = getRegularPriceListCode(companyId)
  const [companyLists, regularWebLists, catalogLists, discountMatrixLists] =
    await Promise.all([
      fetchPriceLists({ CompanyId: companyId }),
      // Sin código regular para la compañía no hay nada que buscar.
      code ? fetchPriceLists({ SearchText: code }) : [],
      fetchPriceLists({
        CompanyId: companyId,
        PriceListTypeId: typePriceLists.catalog,
      }),
      fetchPriceLists({
        CompanyId: companyId,
        PriceListTypeId: typePriceLists.discountmatrix,
      }),
    ])

  return {
    base: companyLists.filter(list => list.DefaultList === true),
    regular: filterRegularPriceLists(regularWebLists, code),
    catalog: catalogLists,
    discountMatrix: discountMatrixLists,
  }
}

export const uploadCatalogImageAction =
  ({ file, entityId }) =>
  async () => {
    const response = await uploadFileService(
      buildCatalogImageFormData({ file, entityId })
    )
    const { data, status } = response
    return status === 200 ? extractUploadedFileUrl(data) : null
  }

// Carga masiva de precios e imágenes (mismo flujo que Listas Rápidas): sube el CSV con mdl12 y lo
// procesa con mdl26/batch/fromBlobFile. Devuelve { readRowsQty, processedRowsQty, errors }.
export const processCatalogBatchFileAction =
  ({ file, email }) =>
  async () => {
    if (!CATALOG_BATCH_TEMPLATE_ID) {
      return { readRowsQty: 0, processedRowsQty: 0, errors: [], failed: true }
    }

    try {
      const uploadResponse = await uploadFileService(
        buildQuickListBatchUploadFormData({
          file,
          batchEntityId: BATCH_ENTITY_ID,
          tenantId: BATCH_TENANT_ID,
          entityType: BATCH_ENTITY_TYPE,
        })
      )
      const { fileUrl, batchProcessId } = extractUploadedBatchFile({
        uploadResponse: uploadResponse?.data,
        batchEntityId: BATCH_ENTITY_ID,
      })

      if (!fileUrl) {
        return { readRowsQty: 0, processedRowsQty: 0, errors: [], failed: true }
      }

      const response = await processCatalogBatchFileService({
        templateId: CATALOG_BATCH_TEMPLATE_ID,
        url: fileUrl,
        batchProcessId,
        email,
      })
      const result = extractCatalogBatchResult(response?.data)
      return { ...result, failed: result.errors.length > 0 }
    } catch (error) {
      return {
        readRowsQty: 0,
        processedRowsQty: 0,
        errors: [getErrorMessage(error)].filter(Boolean),
        failed: true,
      }
    }
  }
